import { useState } from 'react'
import { Box } from '@chakra-ui/react'
import Icon from '@/components/shared/icons'
import useScroll from '@/hooks/useScroll'
import MenuRight from '@/components/menus/menu-right'
import CartModal from '@/components/menus/cart-modal'
import { useDispatch, useSelector } from 'react-redux'
import { AppDispatch, RootState } from '@/store/store'
import { clearCart, toggleCartModal } from '@/store/features/cart'
import { Link } from 'react-router-dom'
import MegaDropdown from '@/components/menus/categories-drop'
import SearchDropdown from '@/components/menus/search-drop'
import CustomersEntity from '@/entities/api/customers/customer'
import ProfilePopup from '@/components/menus/profile-popup'
import { logout } from '@/store/features/auth'

const Navbar = ({
	customer,
	token,
}: {
	customer: CustomersEntity | null
	token: string | null
}) => {
	const { IsScroll } = useScroll()
	const [isOpenMenuRight, setIsOpenMenuRight] = useState<boolean>(false)
	const categories = useSelector(
		(state: RootState) => state.category.categories
	)
	const { totalItems, isCartModalOpen } = useSelector(
		(state: RootState) => state.cart
	)
	const dispatch = useDispatch<AppDispatch>()
	const [searchFocused, setSearchFocused] = useState<boolean>(false)
	const [searchQuery, setSearchQuery] = useState<string>('')
	const [megaDropdownOpen, setMegaDropdownOpen] = useState<boolean>(false)
	const [profilePopupOpen, setProfilePopupOpen] = useState<boolean>(false)

	const getNavbarZIndex = () => {
		if (isCartModalOpen) return 'z-[99998]'
		if (profilePopupOpen) return 'z-[999999]'
		if (searchFocused) return 'z-[999999]'
		if (megaDropdownOpen) return 'z-[999999]'
		if (IsScroll) return 'z-[999]'
		return 'z-[99999]'
	}

	const handleSearchFocus = () => {
		setSearchFocused(true)
		setMegaDropdownOpen(false)
		setProfilePopupOpen(false)
	}

	const handleSearchChange = (value: string) => {
		setSearchQuery(value)
		if (value.trim() && !searchFocused) {
			setSearchFocused(true)
			setMegaDropdownOpen(false)
			setProfilePopupOpen(false)
		}
	}

	const closeSearchDropdown = () => {
		setSearchFocused(false)
	}

	const handleInputClick = () => {
		setSearchFocused(true)
		setMegaDropdownOpen(false)
		setProfilePopupOpen(false)
	}

	const handleCartClick = () => {
		setSearchFocused(false)
		setMegaDropdownOpen(false)
		setProfilePopupOpen(false)
		dispatch(toggleCartModal())
	}

	const handleProfileClick = () => {
		setProfilePopupOpen(!profilePopupOpen)
		setSearchFocused(false)
		setMegaDropdownOpen(false)
	}

	const handleLogout = () => {
		dispatch(logout())
		dispatch(clearCart())
	}

	return (
		<>
			<nav
				className={`w-full h-[10vh] transition-all duration-300 ease-in-out ${getNavbarZIndex()} ${
					IsScroll
						? 'fixed top-0 bg-white/95 backdrop-blur-md shadow-lg border-b border-gray-200'
						: 'static bg-transparent'
				}`}
				style={{
					zIndex: isCartModalOpen
						? 99998
						: profilePopupOpen
						? 999999
						: searchFocused
						? 999999
						: megaDropdownOpen
						? 999999
						: IsScroll
						? 999
						: 99999,
				}}
			>
				<div className='h-full px-4 lg:px-6 xl:px-8 py-2 xl:py-4'>
					<div className='flex items-center justify-between h-full'>
						<div className='flex items-center'>
							<Link to='/'>
								<div className='hidden lg:flex items-center space-x-3'>
									<div className='w-10 h-10 rounded-lg flex items-center justify-center bg-white overflow-hidden'>
										<img
											src='/logo.png'
											alt='SportCenter Logo'
											className='w-8 h-8 object-contain'
										/>
									</div>

									<div>
										<h1 className='text-2xl xl:text-3xl font-black text-gray-900 tracking-tight'>
											<span className='text-red-600'>Sport</span>Center
										</h1>
										<p className='text-xs text-gray-500 font-medium -mt-1'>
											SHOP
										</p>
									</div>
								</div>
							</Link>

							<div className='lg:hidden'>
								<div className='w-8 h-8 bg-gradient-to-br from-red-600 to-red-700 rounded-lg flex items-center justify-center'>
									<svg
										className='w-5 h-5 text-white'
										fill='currentColor'
										viewBox='0 0 20 20'
									>
										<path d='M10.394 2.08a1 1 0 00-.788 0l-7 3a1 1 0 000 1.84L5.25 8.051a.999.999 0 01.356-.257l4-1.714a1 1 0 11.788 1.838L7.667 9.088l1.94.831a1 1 0 00.787 0l7-3a1 1 0 000-1.838l-7-3z' />
									</svg>
								</div>
							</div>
						</div>

						<div className='hidden lg:flex items-center space-x-6 xl:space-x-8 lg:gap-4 flex-1 justify-center'>
							<div
								className='relative'
								style={{ zIndex: megaDropdownOpen ? 999999 : 'auto' }}
							>
								<button
									onClick={() => {
										setMegaDropdownOpen(!megaDropdownOpen)
										setSearchFocused(false)
										setProfilePopupOpen(false)
									}}
									onMouseEnter={() => {
										setMegaDropdownOpen(true)
										setSearchFocused(false)
										setProfilePopupOpen(false)
									}}
									className={`relative text-gray-700 hover:text-red-600 font-semibold transition-colors duration-200 py-2 group flex items-center space-x-1 ${
										megaDropdownOpen ? 'text-red-600' : ''
									}`}
									aria-expanded={megaDropdownOpen}
									aria-haspopup='menu'
								>
									<span>Tienda</span>
									<svg
										className={`w-4 h-4 transition-transform duration-200 ${
											megaDropdownOpen ? 'rotate-180' : ''
										}`}
										fill='none'
										stroke='currentColor'
										viewBox='0 0 24 24'
									>
										<path
											strokeLinecap='round'
											strokeLinejoin='round'
											strokeWidth={2}
											d='M19 9l-7 7-7-7'
										/>
									</svg>
									<span
										className={`absolute bottom-0 left-0 h-0.5 bg-red-600 transition-all duration-300 ${
											megaDropdownOpen ? 'w-full' : 'w-0 group-hover:w-full'
										}`}
									></span>
								</button>

								<div
									className={`absolute top-full left-1/2 transform -translate-x-1/2 w-screen max-w-6xl transition-opacity duration-200 ${
										megaDropdownOpen
											? 'opacity-100 pointer-events-auto'
											: 'opacity-0 pointer-events-none'
									}`}
									style={{
										zIndex: 999999,
										position: 'absolute',
									}}
									onMouseLeave={() => setMegaDropdownOpen(false)}
								>
									<MegaDropdown
										categories={categories}
										isOpen={megaDropdownOpen}
										onClose={() => setMegaDropdownOpen(false)}
									/>
								</div>
							</div>

							<Link
								to='/'
								className='relative text-gray-700 hover:text-red-600 font-semibold transition-colors duration-200 py-2 group'
							>
								Inicio
								<span className='absolute bottom-0 left-0 w-0 h-0.5 bg-red-600 transition-all duration-300 group-hover:w-full'></span>
							</Link>
							<Link
								to='/Nosotros'
								className='relative text-gray-700 hover:text-red-600 font-semibold transition-colors duration-200 py-2 group'
							>
								Nosotros
								<span className='absolute bottom-0 left-0 w-0 h-0.5 bg-red-600 transition-all duration-300 group-hover:w-full'></span>
							</Link>
							<Link
								to='/Promociones'
								className='relative text-gray-700 hover:text-red-600 font-semibold transition-colors duration-200 py-2 group'
							>
								Promociones
								<span className='absolute bottom-0 left-0 w-0 h-0.5 bg-red-600 transition-all duration-300 group-hover:w-full'></span>
								<span className='absolute -top-1 -right-2 bg-red-500 text-white text-xs px-1.5 py-0.5 rounded-full font-bold animate-pulse'>
									HOT
								</span>
							</Link>
							{customer && (
								<Link
									to='/Perfil/compras'
									className='relative text-gray-700 hover:text-red-600 font-semibold transition-colors duration-200 py-2 group'
								>
									Mis compras
									<span className='absolute bottom-0 left-0 w-0 h-0.5 bg-red-600 transition-all duration-300 group-hover:w-full'></span>
								</Link>
							)}
							{customer && (
								<Link
									to='/Perfil/informacion'
									className='relative text-gray-700 hover:text-red-600 font-semibold transition-colors duration-200 py-2 group'
								>
									Mi perfil
									<span className='absolute bottom-0 left-0 w-0 h-0.5 bg-red-600 transition-all duration-300 group-hover:w-full'></span>
								</Link>
							)}
							{!customer && (
								<Link
									to='/Login'
									className='relative text-gray-700 hover:text-red-600 font-semibold transition-colors duration-200 py-2 group'
								>
									Iniciar Sesión
									<span className='absolute bottom-0 left-0 w-0 h-0.5 bg-red-600 transition-all duration-300 group-hover:w-full'></span>
								</Link>
							)}
							{!customer && (
								<Link
									to='/Registro'
									className='bg-red-600 hover:bg-red-700 text-white font-bold px-4 py-2 rounded-lg transition-all duration-200 transform hover:scale-105 shadow-md hover:shadow-lg'
								>
									Registrarse
								</Link>
							)}
						</div>

						<div className='flex items-center space-x-3 lg:space-x-4'>
							<div
								className={`relative transition-all duration-300 ${
									searchFocused
										? 'w-full sm:w-80 lg:w-80'
										: 'w-full sm:w-64 lg:w-72'
								} xl:w-80`}
							>
								<div
									className={`flex items-center bg-gray-100 hover:bg-gray-200 rounded-xl px-3 sm:px-4 py-2 lg:py-2.5 transition-all duration-200 mx-2 sm:mx-3 flex-1 min-w-[140px] ${
										searchFocused
											? 'ring-2 ring-red-500 bg-white shadow-lg'
											: ''
									}`}
								>
									<input
										type='text'
										placeholder='Buscar productos...'
										value={searchQuery}
										onChange={(e) => handleSearchChange(e.target.value)}
										onClick={handleInputClick}
										onFocus={handleSearchFocus}
										className='w-full bg-transparent text-gray-700 placeholder-gray-500 outline-none text-sm font-medium min-w-0'
									/>
									<button
										className='text-gray-500 hover:text-red-600 transition-colors duration-200 p-1 flex-shrink-0'
										onClick={handleInputClick}
									>
										<Icon
											icon='search'
											color='dark'
											size='lg'
											aria_label='search'
										/>
									</button>
								</div>

								<SearchDropdown
									isOpen={searchFocused}
									onClose={closeSearchDropdown}
									searchQuery={searchQuery}
									onSearchChange={handleSearchChange}
								/>
							</div>

							<div className='hidden lg:flex items-center space-x-2'>
								<div className='relative'>
									<button
										onClick={handleCartClick}
										className={`relative p-2 text-gray-600 hover:text-red-600 hover:bg-red-50 rounded-lg transition-all duration-200 group ${
											isCartModalOpen ? 'bg-red-50 text-red-600' : ''
										}`}
									>
										<Icon
											icon='shopping'
											color='dark'
											size='xl'
											aria_label='shopping cart'
										/>
										{totalItems > 0 && (
											<span className='absolute -top-1 -right-1 bg-red-500 text-white text-xs w-5 h-5 rounded-full flex items-center justify-center font-bold group-hover:scale-110 transition-transform duration-200'>
												{totalItems > 99 ? '99+' : totalItems}
											</span>
										)}
									</button>
									{isCartModalOpen && (
										<CartModal
											customerId={customer?._id ?? ''}
											token={token ?? ''}
										/>
									)}
								</div>

								{customer && (
									<div className='relative'>
										<button
											onClick={handleProfileClick}
											className={`p-2 text-gray-600 hover:text-red-600 hover:bg-red-50 rounded-lg transition-all duration-200 ${
												profilePopupOpen ? 'bg-red-50 text-red-600' : ''
											}`}
										>
											<Icon
												icon='user'
												color='dark'
												size='xl'
												aria_label='profile'
											/>
										</button>

										{profilePopupOpen && (
											<ProfilePopup
												isOpen={profilePopupOpen}
												onClose={() => setProfilePopupOpen(false)}
												customer={customer}
												totalItems={totalItems}
												onLogout={handleLogout}
											/>
										)}
									</div>
								)}
							</div>

							<div className='lg:hidden relative'>
								<button
									onClick={handleCartClick}
									className={`relative mr-1 md:mr-0 cursor-pointer p-2 text-gray-600 hover:text-red-600 hover:bg-red-50 rounded-lg transition-all duration-200 group ${
										isCartModalOpen ? 'bg-red-50 text-red-600' : ''
									}`}
								>
									<Icon
										icon='shopping'
										color='dark'
										size='xl'
										aria_label='shopping cart'
									/>
									{totalItems > 0 && (
										<span className='absolute -top-1 -right-1 bg-red-500 text-white text-xs w-5 h-5 rounded-full flex items-center justify-center font-bold group-hover:scale-110 transition-transform duration-200'>
											{totalItems > 99 ? '99+' : totalItems}
										</span>
									)}
								</button>
								{isCartModalOpen && (
									<CartModal
										customerId={customer?._id ?? ''}
										token={token ?? ''}
									/>
								)}
							</div>

							{!isOpenMenuRight && (
								<button
									className='lg:hidden p-2 text-gray-600 hover:text-red-600 hover:bg-red-50 rounded-lg transition-all duration-200'
									onClick={() => setIsOpenMenuRight(true)}
								>
									<Icon
										icon='bar'
										color='dark'
										size='xl'
										aria_label='open menu'
									/>
								</button>
							)}
						</div>
					</div>
				</div>
			</nav>

			{(megaDropdownOpen || searchFocused) &&
				!isCartModalOpen &&
				!profilePopupOpen && (
					<div
						className='fixed inset-0 z-[999998] bg-transparent'
						onClick={() => {
							setMegaDropdownOpen(false)
							setSearchFocused(false)
						}}
						style={{
							zIndex: 999998,
							pointerEvents: 'auto',
						}}
					/>
				)}

			<Box>
				<MenuRight
					customer={customer}
					token={token}
					onLogout={handleLogout}
					isOpen={isOpenMenuRight}
					onClose={() => setIsOpenMenuRight(false)}
				/>
			</Box>
		</>
	)
}

export default Navbar
